import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Badge
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Business as VendorIcon,
  ShoppingCart as OrderIcon,
  RequestQuote as RFQIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationIcon,
  ArrowForward as ArrowForwardIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ProcurementDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalSpend: 0,
      totalOrders: 0,
      activeVendors: 0,
      pendingRFQs: 0,
      lowStockItems: 0
    },
    trends: {
      monthlySpend: [],
      orderVolume: [],
      savingsData: []
    },
    recentActivity: [],
    alerts: {
      lowStockItems: [],
      overduePOs: [],
      pendingApprovals: []
    },
    topVendors: [],
    categorySpend: [],
    pendingApprovals: []
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [overviewRes, trendsRes, activityRes, alertsRes, vendorsRes, categoriesRes, approvalsRes, vendorStatsRes, rfqStatsRes] = await Promise.all([
        api.get('/procurement/dashboard/overview'),
        api.get('/procurement/dashboard/trends'),
        api.get('/procurement/dashboard/activity'),
        api.get('/procurement/dashboard/alerts'),
        api.get('/vendors/top'),
        api.get('/procurement/dashboard/categories'),
        api.get('/purchase-orders?status=pending_approval&limit=5'),
        api.get('/vendors/stats'),
        api.get('/rfqs/stats')
      ]);

      // Map backend overview fields to UI-friendly keys
      const overview = {
        totalSpend: overviewRes.data?.totalSpend || 0,
        totalOrders: overviewRes.data?.totalPOs || 0,
        activeVendors: vendorStatsRes.data?.activeVendors || 0,
        pendingRFQs: rfqStatsRes.data?.publishedRFQs || 0,
        lowStockItems: overviewRes.data?.lowStockItems || 0
      };

      // Transform trends to expected shape
      const monthlySpend = (trendsRes.data || []).map(t => ({
        month: t.month,
        amount: t.totalSpend
      }));
      const orderVolume = (trendsRes.data || []).map(t => ({
        month: t.month,
        orders: t.orderCount
      }));

      // Transform category spend to pie chart format
      const categorySpend = (categoriesRes.data || []).map(c => ({
        name: c._id,
        value: c.totalSpend
      }));

      // Adapt top vendors to realistic fields
      const topVendors = (vendorsRes.data || []).map(v => ({
        ...v,
        displayRating: v?.performance?.rating || 0,
        displayContracts: v?.totalContracts || 0
      }));

      setDashboardData({
        overview,
        trends: { monthlySpend, orderVolume },
        recentActivity: activityRes.data,
        alerts: {
          lowStockItems: alertsRes.data.lowStockItems || [],
          overduePOs: alertsRes.data.overduePOs || [],
          pendingApprovals: alertsRes.data.pendingApprovals || []
        },
        topVendors,
        categorySpend,
        pendingApprovals: approvalsRes.data
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };





  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Procurement Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Overview of procurement activities and performance metrics
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Spend
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(dashboardData.overview.totalSpend)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" color="success.main">
                      +12.5%
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <MoneyIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Purchase Orders
                  </Typography>
                  <Typography variant="h5">
                    {dashboardData.overview.totalOrders}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" color="success.main">
                      +8.3%
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <OrderIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Vendors
                  </Typography>
                  <Typography variant="h5">
                    {dashboardData.overview.activeVendors}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" color="success.main">
                      +5.2%
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <VendorIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending RFQs
                  </Typography>
                  <Typography variant="h5">
                    {dashboardData.overview.pendingRFQs}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <ScheduleIcon color="warning" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" color="warning.main">
                      Needs attention
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <RFQIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Low Stock Items
                  </Typography>
                  <Typography variant="h5">
                    <Badge badgeContent={dashboardData.overview.lowStockItems} color="error">
                      {dashboardData.overview.lowStockItems}
                    </Badge>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <WarningIcon color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" color="error.main">
                      Reorder needed
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <InventoryIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Monthly Spend Trend */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Spend Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dashboardData.trends.monthlySpend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), 'Spend']} />
                    <Area 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#8884d8" 
                      fill="#8884d8" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Category Spend Distribution */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Spend by Category
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboardData.categorySpend}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {dashboardData.categorySpend.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), 'Spend']} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Activity
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon />}>
                  View All
                </Button>
              </Box>
              <List dense>
                {(dashboardData.recentActivity?.recentPOs || []).slice(0, 3).map((po, index) => (
                  <ListItem key={`po-${index}`} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <OrderIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`PO ${po.orderNumber} - ${po.vendor?.name || 'Unknown Vendor'}`}
                      secondary={`${formatCurrency(po.totalAmount)} - ${new Date(po.createdAt).toLocaleDateString()}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {(dashboardData.recentActivity?.recentItems || []).slice(0, 2).map((item, index) => (
                  <ListItem key={`item-${index}`} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <InventoryIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${item.name} - ${item.category}`}
                      secondary={`Added ${new Date(item.createdAt).toLocaleDateString()}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Alerts & Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Alerts & Notifications
                </Typography>
                <Badge 
                  badgeContent={
                    ((dashboardData.alerts.lowStockItems || []).length) +
                    ((dashboardData.alerts.overduePOs || []).length) +
                    ((dashboardData.alerts.pendingApprovals || []).length)
                  } 
                  color="error"
                >
                  <NotificationIcon />
                </Badge>
              </Box>
              <List dense>
                {/* Low Stock Items */}
                {(dashboardData.alerts.lowStockItems || []).slice(0, 2).map((item, index) => (
                  <ListItem key={`low-stock-${index}`} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WarningIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Low Stock: ${item.name}`}
                      secondary={`Current: ${item.inventory?.currentStock || 0}, Reorder: ${item.inventory?.reorderLevel || 0}`}
                    />
                  </ListItem>
                ))}
                {/* Overdue POs */}
                {(dashboardData.alerts.overduePOs || []).slice(0, 2).map((po, index) => (
                  <ListItem key={`overdue-${index}`} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Overdue PO: ${po.orderNumber}`}
                      secondary={`Vendor: ${po.vendor?.name || 'Unknown'}`}
                    />
                  </ListItem>
                ))}
                {/* Pending Approvals */}
                {(dashboardData.alerts.pendingApprovals || []).slice(0, 1).map((po, index) => (
                  <ListItem key={`pending-${index}`} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ScheduleIcon color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Pending Approval: ${po.orderNumber}`}
                      secondary={`Amount: $${po.totalAmount?.toLocaleString() || '0'} - ${po.vendor?.name || 'Unknown'}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Top Vendors */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Top Vendors
                </Typography>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />} 
                  onClick={() => navigate('/vendors')}
                >
                  View All
                </Button>
              </Box>
              <List dense>
                {dashboardData.topVendors.slice(0, 5).map((vendor) => (
                  <ListItem key={vendor._id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {vendor.name.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={vendor.name}
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption">
                            Rating: {vendor.displayRating?.toFixed?.(1) ?? vendor.displayRating}
                          </Typography>
                          <Chip 
                            label={`${vendor.displayContracts || 0} contracts`} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Approvals Section */}
      {dashboardData.pendingApprovals.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Pending Approvals
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => navigate('/purchase-orders')}
              >
                Review All
              </Button>
            </Box>
            <Grid container spacing={2}>
              {dashboardData.pendingApprovals.map((order) => (
                <Grid item xs={12} sm={6} md={4} key={order._id}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'warning.main' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2">
                        PO #{order.orderNumber}
                      </Typography>
                      <Chip label="Pending" color="warning" size="small" />
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {order.vendor?.name}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(order.totalAmount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(order.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" color="success">
                        Approve
                      </Button>
                      <Button size="small" variant="outlined" color="error">
                        Reject
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<OrderIcon />}
                onClick={() => navigate('/purchase-orders/create')}
                sx={{ py: 2 }}
              >
                Create Purchase Order
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RFQIcon />}
                onClick={() => navigate('/rfqs/create')}
                sx={{ py: 2 }}
              >
                Create RFQ
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<VendorIcon />}
                onClick={() => navigate('/vendors/create')}
                sx={{ py: 2 }}
              >
                Add Vendor
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/procurement-items/create')}
                sx={{ py: 2 }}
              >
                Manage Inventory
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProcurementDashboard;